import type { Site } from './cluster';
import type { LatLon } from './geo';
import type { Network } from './operators';

export type Measurement = {
  id: string;
  takenAt: string;
  network: Network;
  device: string;
  radio: 'NR' | 'LTE' | string;
  band?: string;
  cellId?: string;
  rsrp?: number;
  rsrq?: number;
  sinr?: number;
  downMbps?: number;
  upMbps?: number;
  headingDeg?: number;
  lat?: number;
  lon?: number;
  spot?: string;
  note?: string;
  siteId?: string;
};

export type Settings = { home?: LatLon; homeHeightM?: number };

/** The Cloudflare Access session is gone: the request was redirected to the login page. */
export class AuthExpiredError extends Error {
  constructor() {
    super('Sessione scaduta: accedi di nuovo per sincronizzare');
    this.name = 'AuthExpiredError';
  }
}

export function createApi(fetchFn: typeof fetch = (...a) => fetch(...a)) {
  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetchFn('/api' + path, {
      method,
      credentials: 'include',
      redirect: 'manual',
      headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (res.type === 'opaqueredirect' || (res.status >= 300 && res.status < 400) || res.status === 401 || res.status === 403) {
      throw new AuthExpiredError();
    }
    if (res.status === 204) return undefined as T;
    const ct = res.headers.get('Content-Type') ?? '';
    if (!ct.includes('application/json')) throw new AuthExpiredError();
    const data = await res.json();
    if (!res.ok) throw new Error((data as { error?: string })?.error ?? `Errore ${res.status}`);
    return data as T;
  }
  const id = (s: string) => '/' + encodeURIComponent(s);
  return {
    getSites: () => call<Site[]>('GET', '/sites'),
    putSite: (s: Site) => call<void>('PUT', '/sites' + id(s.id), s),
    deleteSite: (siteId: string) => call<void>('DELETE', '/sites' + id(siteId)),
    getSettings: () => call<Settings>('GET', '/settings'),
    putSettings: (s: Settings) => call<void>('PUT', '/settings', s),
    getMeasurements: () => call<Measurement[]>('GET', '/measurements'),
    putMeasurement: (m: Measurement) => call<void>('PUT', '/measurements' + id(m.id), m),
    deleteMeasurement: (mId: string) => call<void>('DELETE', '/measurements' + id(mId)),
  };
}

export const api = createApi();
export type Api = ReturnType<typeof createApi>;
